import React, {useEffect, useState, useRef} from 'react';
import {useLocation} from 'react-router-dom';

import {text} from '../text';
import {hooks} from '../hooks';
import {utils} from '../utils';
import {theme} from '../constants';
import {components} from '../components';
import {svg} from '../assets/svg';
import axiosInstance from '../services/axiosInstance';
import { savePhotoForQueue, loadPhotoAsFile } from '../services/offlineFileService';
import { offlineQueue } from '../services/offlineQueueService';
import { submissionDraft } from '../services/taskSubmissionDraftService';
import { setPendingCount } from '../store/slices/offlineSlice';
import {custom} from '../custom';
import { FaFileExcel, FaFileWord, FaFilePdf, FaFilePowerpoint, FaFileArchive, FaFileAlt } from 'react-icons/fa';
import { MdCameraAlt, MdClose, MdDelete, MdCheckCircle, MdError, MdWarning } from 'react-icons/md';
import { useLanguage } from '../context/LanguageContext';
import { compressImage } from '../utils/imageUtils';
import { getCurrentLocation } from '../utils/locationUtils';

const FileExcelIcon = FaFileExcel as any;
const FileWordIcon = FaFileWord as any;
const FilePdfIcon = FaFilePdf as any;
const FilePowerpointIcon = FaFilePowerpoint as any;
const FileArchiveIcon = FaFileArchive as any;
const FileAltIcon = FaFileAlt as any;
const CameraIcon = MdCameraAlt as any;
const CloseIcon = MdClose as any;
const DeleteIcon = MdDelete as any;
const CheckCircleIcon = MdCheckCircle as any;
const ErrorIcon = MdError as any;
const WarningIcon = MdWarning as any;

export const TaskSubmission: React.FC = () => {
  const {state} = useLocation();
  const navigate = hooks.useAppNavigate();
  const task = state?.task;
  const [fullTask, setFullTask] = useState(state?.task);
  const { t } = useLanguage();
  const { authSlice, offlineSlice } = hooks.useAppSelector((s: any) => s);
  const { isOnline } = offlineSlice;
  const dispatch = hooks.useAppDispatch();

  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<{[key: number]: (File | any)[]}>({});
  const [textValues, setTextValues] = useState<{[key: number]: string}>({});
  const [location, setLocation] = useState<{lat: number | null, lng: number | null}>({lat: null, lng: null});
  const [address, setAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingProgress, setIsSavingProgress] = useState(false);
  const [isDraftLoading, setIsDraftLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [taskTypes, setTaskTypes] = useState<string[]>([]);
  const [draftFeedback, setDraftFeedback] = useState<any | null>(null);
  const [attachmentSettings, setAttachmentSettings] = useState<any[]>([]);
  const [activeAttachmentId, setActiveAttachmentId] = useState<number | null>(null);
  const [additionalFiles, setAdditionalFiles] = useState<(File | any)[]>([]);
  const [activeAttachmentSetting, setActiveAttachmentSetting] = useState<any | null>(null);
  const [tempFiles, setTempFiles] = useState<(File | any)[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [tempPreviewUrl, setTempPreviewUrl] = useState<string | null>(null);

  const draftLoaded = useRef(false);
  const offlineDraftMerged = useRef(false);
  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning';
    errorDetail?: string;
    onClose?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'success' });
  const [errorDetailExpanded, setErrorDetailExpanded] = useState(false);

  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'http://localhost:5000/';

  const fileInputRef = useRef<HTMLInputElement>(null);
  const additionalFileInputRef = useRef<HTMLInputElement>(null);
  const tempFileInputRef = useRef<HTMLInputElement>(null);

  // Simpan offline_ref files ke draft store (strip previewUrl agar tidak tersimpan blob URL)
  const persistDraft = async (
    newAttachments: Record<string | number, any[]>,
    newAdditionalFiles: any[]
  ) => {
    if (!task?.id || !authSlice.userId) return;
    const draftAttachments: Record<string, any[]> = {};
    for (const [key, files] of Object.entries(newAttachments)) {
      const storable = files
        .filter((f: any) => f.offline_ref)
        .map((f: any) => { const { previewUrl, ...rest } = f; return rest; });
      if (storable.length > 0) draftAttachments[key] = storable;
    }
    const draftAdditional = newAdditionalFiles
      .filter((f: any) => f.offline_ref)
      .map((f: any) => { const { previewUrl, ...rest } = f; return rest; });
    await submissionDraft.save({
      task_id: task.id,
      user_id: String(authSlice.userId),
      attachments: draftAttachments,
      additional_files: draftAdditional,
      saved_at: new Date().toISOString(),
    });
  };

  useEffect(() => {
    if (!task) {
      navigate(-1);
      return;
    }

    // Ambil lokasi device saat screen dimuat
    const fetchLocation = async () => {
      try {
        const locationData = await getCurrentLocation({ addressFormat: 'full' });
        setLocation({ lat: locationData.lat, lng: locationData.lng });
        setAddress(locationData.address);
      } catch (error: any) {
        setLocationError(error.message || 'Failed to get location.');
      }
    };
    
    fetchLocation();

    // Fetch task details terbaru untuk memastikan data feedback termuat
    const fetchTaskDetails = async () => {
      if (task?.id) {
        try {
          const response = await axiosInstance.get(`/tasks/${task.id}`);
          setFullTask(response.data);
        } catch (error) {
          console.error("Failed to fetch latest task details", error);
        }
      }
    };
    fetchTaskDetails();
  }, []);

  // Load draft data when fullTask is available
  useEffect(() => {
    if (fullTask?.feedbacks) {
      const draft = fullTask.feedbacks.find((f: any) => f.review_status === 'draft');      
      if (draft && !draftLoaded.current) {
        draftLoaded.current = true;
        setDraftFeedback(draft);
        setDescription(draft.description || '');
        
        const initialTextValues: {[key: number]: string} = {};
        const initialAttachments: { [key: number]: any[] } = {};
        const initialAdditionalFiles: any[] = [];

        if (draft.images) {
          draft.images.forEach((att: any) => {
            if (att.response_value && att.task_checklist_id) {
              initialTextValues[att.task_checklist_id] = att.response_value;
            } else if (att.file_path) {
              const serverFile = {
                id: att.id,
                name: att.file_path.split(/[/\\]/).pop(),
                path: att.file_path,
                type: att.file_type,
                isDraft: true,
                note: att.note || '',
                timing: att.timing,
              };
              if (att.task_checklist_id) {
                if (!initialAttachments[att.task_checklist_id]) {
                  initialAttachments[att.task_checklist_id] = [];
                }
                initialAttachments[att.task_checklist_id].push(serverFile);
              } else {
                initialAdditionalFiles.push(serverFile);
              }
            }
          });
        }
        setTextValues(initialTextValues);
        setAttachments(initialAttachments);
        setAdditionalFiles(initialAdditionalFiles);
      }
    }

    // Merge offline draft (foto yang disimpan saat offline sebelumnya)
    const taskId = task?.id || fullTask?.id;
    if (taskId && authSlice.userId && !offlineDraftMerged.current) {
      offlineDraftMerged.current = true;
      submissionDraft.get(taskId, String(authSlice.userId)).then((od) => {
        if (!od) return;
        setAttachments(prev => {
          const merged: Record<string, any[]> = { ...prev };
          for (const [key, files] of Object.entries(od.attachments)) {
            const restored = (files as any[]).map(f => ({
              ...f,
              previewUrl: f.photo_ref?.type === 'base64' ? f.photo_ref.value : '',
            }));
            merged[key] = [...(merged[key] || []), ...restored];
          }
          return merged;
        });
        if (od.additional_files?.length) {
          setAdditionalFiles(prev => [
            ...prev,
            ...od.additional_files.map((f: any) => ({
              ...f,
              previewUrl: f.photo_ref?.type === 'base64' ? f.photo_ref.value : '',
            })),
          ]);
        }
      });
    }
  }, [fullTask, imageBaseUrl]);

  // Fetch task types from the API
  useEffect(() => {
    const fetchTaskTypes = async () => {
      try {
        const response = await axiosInstance.get('/settings/task-types');
        setTaskTypes(response.data);
      } catch (error) {
        console.error('Failed to fetch task types:', error);
      }
    };
    fetchTaskTypes();
  }, []);

  // Fetch Attachment Settings based on Task Type
  useEffect(() => {
    // Pastikan kita menggunakan data required_attachments langsung dari detail task (termasuk jika array kosong)
    // Kita tunggu sampai detailnya utuh di-fetch oleh fetchTaskDetails
    if (fullTask && fullTask.required_attachments !== undefined) {
      setAttachmentSettings(fullTask.required_attachments);
    }
  }, [fullTask]);
  
  const showAlert = (title: string, message: string, type: 'success' | 'error' | 'warning' = 'success', onClose?: () => void, errorDetail?: string) => {
    setErrorDetailExpanded(false);
    setAlertConfig({
      isOpen: true,
      title,
      message,
      type,
      errorDetail,
      onClose,
    });
  };

  const hideAlert = () => {
    const onClose = alertConfig.onClose;
    setAlertConfig((prev) => ({ ...prev, isOpen: false }));
    setErrorDetailExpanded(false);
    if (onClose) {
      onClose();
    }
  };

  const getErrorMessage = (error: any, defaultMsg: string) => {
    if (error.response) {
      if (error.response.status === 413) {
        return 'Total file size is too large. The server rejected the request (Payload Too Large).';
      }
      if (error.response.status === 502 || error.response.status === 504) {
        return 'Server did not respond (Gateway Timeout/Bad Gateway). Please try again in a moment.';
      }
      return error.response.data?.message || defaultMsg;
    } else if (error.request) {
      if (error.code === 'ECONNABORTED' || (error.message && error.message.toLowerCase().includes('timeout'))) {
        return 'Connection timed out. Please ensure your internet connection is stable.';
      }
      if (error.message === 'Network Error') {
        return 'Failed to connect to the network. Please check your internet connection.';
      }
      return 'No response from server. Please ensure your internet is active.';
    }
    return error.message || defaultMsg;
  };

  const handleAttachmentClick = (setting: any, timing?: string) => {
    setActiveAttachmentSetting({ ...setting, timing });
    const existingFiles = attachments[setting.id] || [];
    if (timing) {
      setTempFiles(existingFiles.filter(f => f.timing === timing));
    } else {
      setTempFiles(existingFiles);
    }
  };

  const handleTextChange = (id: number, value: string) => {
    setTextValues(prev => ({...prev, [id]: value}));
  };

  const removeAttachment = (id: number) => {
    setAttachments(prev => {
      const newAttachments = { ...prev };
      delete newAttachments[id];
      return newAttachments;
    });
  };

  const handleTempFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      let file = e.target.files[0];

      if (file.type.startsWith('image/')) {
        setIsCompressing(true);
        try {
          file = await compressImage(file);
        } catch (error) {
          console.error("Compression error:", error);
        } finally {
          setIsCompressing(false);
        }
      }
      
      const maxFileSizeMB = 5;

      if (!file.type.startsWith('image/')) {
          showAlert('Invalid File Type', `File "${file.name}" is not an image. Only image files are allowed.`, 'error');
          e.target.value = '';
          return;
      }
      if (file.size > maxFileSizeMB * 1024 * 1024) {
          showAlert('File Too Large', `File "${file.name}" exceeds the maximum size of ${maxFileSizeMB}MB.`, 'error');
          e.target.value = '';
          return;
      }

      setTempFiles(prev => [...prev, file]);
    }
  };

  const removeTempFile = (index: number) => {
    setTempFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && activeAttachmentId !== null) {
      const newFiles = Array.from(e.target.files);
      
      const processedFiles = await Promise.all(newFiles.map(async (file) => {
        if (file.type.startsWith('image/')) {
          try {
            return await compressImage(file);
          } catch (error) {
            console.error("Compression error:", error);
          }
        }
        return file;
      }));

      const maxFileSizeMB = 5;

      const validFiles = processedFiles.filter(file => {
          if (!file.type.startsWith('image/')) {
              showAlert('Invalid File Type', `File "${file.name}" is not an image. Only image files are allowed.`, 'error');
              return false;
          }
          if (file.size > maxFileSizeMB * 1024 * 1024) {
              showAlert('File Too Large', `File "${file.name}" exceeds the maximum size of ${maxFileSizeMB}MB.`, 'error');
              return false;
          }
          return true;
      });

      setAttachments(prev => ({
        ...prev,
        [activeAttachmentId]: [...(prev[activeAttachmentId] || []), ...validFiles]
      }));
    }
  };

  const handleAdditionalFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      // Offline: simpan file ke offline storage agar tidak hilang jika app ditutup
      if (!isOnline) {
        const newFiles = Array.from(e.target.files);
        const processedFiles: File[] = await Promise.all(newFiles.map(async (file) => {
          if (file.type.startsWith('image/')) {
            try { return await compressImage(file, 1080, 1080, 0.7); } catch { return file; }
          }
          return file;
        }));
        const maxFileSizeMB = 5;
        const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf',
          'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'text/plain'];
        const validFiles = processedFiles.filter(file => {
          if (file.size > maxFileSizeMB * 1024 * 1024) {
            showAlert('File Too Large', `File "${file.name}" exceeds the maximum size of ${maxFileSizeMB}MB.`, 'error');
            return false;
          }
          if (!allowedTypes.includes(file.type)) {
            showAlert('Invalid File Type', `File "${file.name}" has an unsupported file type.`, 'error');
            return false;
          }
          return true;
        });
        const offlineRefs: any[] = [];
        for (const file of validFiles) {
          try {
            const photoRef = await savePhotoForQueue(file);
            offlineRefs.push({
              offline_ref: true,
              photo_ref: photoRef,
              name: file.name,
              type: file.type,
              note: '',
              previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
            });
          } catch {
            offlineRefs.push(file); // fallback: simpan sebagai File biasa
          }
        }
        const newAdditional = [...additionalFiles, ...offlineRefs];
        await persistDraft(attachments, newAdditional);
        setAdditionalFiles(newAdditional);
        if (additionalFileInputRef.current) additionalFileInputRef.current.value = '';
        return;
      }

      setIsDraftLoading(true);
      const newFiles = Array.from(e.target.files);
      
      const processedFiles = await Promise.all(newFiles.map(async (file) => {
        if (file.type.startsWith('image/')) {
          try {
            return await compressImage(file);
          } catch (error) {
            console.error("Compression error:", error);
          }
        }
        return file;
      }));

      const maxFileSizeMB = 5;
      const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/plain'];
      
      const validFiles = processedFiles.filter(file => {
          if (file.size > maxFileSizeMB * 1024 * 1024) {
              showAlert('File Too Large', `File "${file.name}" exceeds the maximum size of ${maxFileSizeMB}MB.`, 'error');
              return false;
          }
          if (!allowedTypes.includes(file.type)) {
              showAlert('Invalid File Type', `File "${file.name}" has an unsupported file type. Only image, PDF, Word, Excel, and Text files are allowed.`, 'error');
              return false;
          }
          return true;
      });

      const draftUploadedFiles: any[] = [];
      for (const file of validFiles) {
        try {
          const formData = new FormData();
          formData.append('attachment', file);
          if (location.lat !== null && location.lng !== null) {
            formData.append('latitude', location.lat.toString());
            formData.append('longitude', location.lng.toString());
            formData.append('address', address || '');
          }

          const response = await axiosInstance.post(`/tasks/${task.id}/draft-attachments`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => {
              setUploadProgress(Math.round((e.loaded * 100) / (e.total ?? e.loaded)));
            }
          });

          draftUploadedFiles.push({
            id: response.data.attachment.id,
            name: response.data.attachment.file_path.split(/[/\\]/).pop(),
            path: response.data.attachment.file_path,
            type: response.data.attachment.file_type,
            isDraft: true,
            note: '',
          });
        } catch(err: any) {
          console.error("Failed to upload additional draft file:", err);
          const errorMsg = getErrorMessage(err, `Failed to upload ${file.name}`);
          showAlert(t.common?.error || 'Error', errorMsg, 'error', undefined, err?.response?.data?.error || err?.message);
        }
      }

      setAdditionalFiles(prev => [...prev, ...draftUploadedFiles]);
      setUploadProgress(null);
      setIsDraftLoading(false);
    }
  };

  const removeAdditionalFile = async (index: number) => {
    const fileToRemove = additionalFiles[index];
    if (fileToRemove && fileToRemove.isDraft) {

      try {
        await axiosInstance.delete(`/tasks/${task.id}/draft-attachments/${fileToRemove.id}`);
      } catch (e) {
        console.error("Failed to delete draft additional file:", e);
      } finally {
        setIsDraftLoading(false);
      }
    }
    setAdditionalFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(ext || '')) return <FilePdfIcon size={24} color="#EF4444" />;
    if (['xls', 'xlsx', 'csv'].includes(ext || '')) return <FileExcelIcon size={24} color="#10B981" />;
    if (['doc', 'docx'].includes(ext || '')) return <FileWordIcon size={24} color="#3B82F6" />;
    if (['ppt', 'pptx'].includes(ext || '')) return <FilePowerpointIcon size={24} color="#F59E0B" />;
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext || '')) return <FileArchiveIcon size={24} color="#6B7280" />;
    return <FileAltIcon size={24} color="#9CA3AF" />;
  };

  const handleSubmit = async () => {
    if (isLoading) return;

    if (!description) {
      showAlert(t.common?.warning || 'Warning', t.taskSubmission?.alerts?.missingDescription || 'Please enter a description', 'warning');
      return;
    }

    if (location.lat === null || location.lng === null) {
      showAlert(t.common?.warning || 'Warning', (t.taskSubmission?.alerts?.locationRequired || 'Location is required. ') + (locationError || t.taskSubmission?.fetchingLocation), 'warning');
      return;
    }

    const missingFileAttachments = attachmentSettings.filter(setting => {
      if (setting.input_type === 'text') return false;
      const files = attachments[setting.id] || [];
      if (setting.is_maintenance) {
        const hasBefore = files.some(f => f.timing === 'before');
        const hasAfter = files.some(f => f.timing === 'after');
        return !(hasBefore && hasAfter);
      } else {
        return files.length === 0;
      }
    });
    const missingTextAttachments = attachmentSettings.filter(setting => 
      setting.input_type === 'text' && (!textValues[setting.id] || textValues[setting.id].trim() === '')
    );

    if (missingFileAttachments.length > 0 || missingTextAttachments.length > 0) {
      const missing = [...missingFileAttachments, ...missingTextAttachments];
      showAlert(t.common?.warning || 'Warning', `${t.taskSubmission?.alerts?.missingInformation || 'Please complete required fields: '}${missing.map(s => s.title).join(', ')}`, 'warning');
      return;
    }

    if (!isOnline) {
      try {
        setIsLoading(true);
        const isNewFile = (file: any) => file instanceof File;
        const isOfflineRef = (file: any) => !!file?.offline_ref;

        // Simpan semua file ke offline storage (file baru disimpan, offline_ref sudah tersimpan)
        const queuedAttachments: any[] = [];
        for (const [key, files] of Object.entries(attachments)) {
          for (const file of files.filter(isNewFile)) {
            const photoRef = await savePhotoForQueue(file as File);
            queuedAttachments.push({
              photo_ref: photoRef,
              task_checklist_id: key,
              note: (file as any).note || '',
              timing: (file as any).timing || '',
            });
          }
          // File yang sudah disimpan saat saveAttachment offline — gunakan photo_ref langsung
          for (const file of files.filter(isOfflineRef)) {
            queuedAttachments.push({
              photo_ref: file.photo_ref,
              task_checklist_id: key,
              note: file.note || '',
              timing: file.timing || '',
            });
          }
        }
        const queuedAdditional: any[] = [];
        for (const file of additionalFiles) {
          if (isNewFile(file)) {
            const photoRef = await savePhotoForQueue(file as File);
            queuedAdditional.push({ photo_ref: photoRef, note: (file as any).note || '' });
          } else if (isOfflineRef(file)) {
            queuedAdditional.push({ photo_ref: file.photo_ref, note: file.note || '' });
          }
        }

        const retainedIds = Object.values(attachments).flat()
          .filter((f: any) => f?.isDraft).map((f: any) => f.id);
        const retainedUpdates = Object.values(attachments).flat()
          .filter((f: any) => f?.isDraft).map((f: any) => ({ id: f.id, note: f.note || '' }));

        await offlineQueue.add({
          user_id: authSlice.userId || '',
          action_type: 'task_report_submit',
          payload: {
            task_id: task.id,
            description,
            latitude: location.lat ?? 0, longitude: location.lng ?? 0, address: address || '',
            attachments: queuedAttachments,
            additional_files: queuedAdditional,
            text_attachments: Object.entries(textValues)
              .filter(([, v]) => v)
              .map(([key, value]) => ({ task_checklist_id: key, value })),
            retained_attachment_ids: retainedIds,
            retained_attachment_updates: retainedUpdates,
          },
        });
        dispatch(setPendingCount(await offlineQueue.getPendingCount()));
        await submissionDraft.clear(task.id);
        showAlert(t.common?.success || 'Success', t.taskSubmission?.alerts?.offlineSaved || 'Laporan tersimpan offline dan akan dikirim saat terkoneksi.', 'success', () => navigate('/Task'));
      } catch (err: any) {
        showAlert(t.common?.error || 'Error', err.message || 'Gagal menyimpan offline', 'error');
      } finally {
        setIsLoading(false);
      }
      return;
    }

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('description', description);
      formData.append('latitude', location.lat.toString());
      formData.append('longitude', location.lng.toString());
      formData.append('address', address || '');
      
      const isNewFile = (file: any) => file instanceof File;
      const isDraftFile = (file: any) => file?.isDraft === true;
      const isOfflineRef = (file: any) => !!file?.offline_ref;

      // Get IDs of draft files to retain & their notes
      const retainedAttachmentIds: number[] = [];
      const retainedAttachmentUpdates: {id: number, note: string}[] = [];

      const processDraftFile = (file: any) => {
        if (isDraftFile(file)) {
          retainedAttachmentIds.push(file.id);
          retainedAttachmentUpdates.push({ id: file.id, note: (file as any).note || '' });
        }
      };

      Object.values(attachments).flat().forEach(processDraftFile);
      additionalFiles.forEach(processDraftFile);

      if (retainedAttachmentIds.length > 0) {
        retainedAttachmentIds.forEach(id => {
          formData.append('retained_attachment_ids', id.toString());
        });
        formData.append('retained_attachment_updates', JSON.stringify(retainedAttachmentUpdates));
      } else {
        formData.append('retained_attachment_ids', '');
      }

      // Append structured attachments — file baru (instanceof File)
      Object.entries(attachments).forEach(([key, files]) => {
          files.filter(isNewFile).forEach(file => {
              formData.append('attachments', file);
              formData.append('task_checklist_ids', key);
              formData.append('attachment_notes', (file as any).note || '');
              formData.append('attachment_timings', (file as any).timing || '');
          });
      });

      // Append offline_ref attachments — disimpan saat offline, load dari offline storage
      for (const [key, files] of Object.entries(attachments)) {
        for (const file of files.filter(isOfflineRef)) {
          const loadedFile = await loadPhotoAsFile(file.photo_ref);
          formData.append('attachments', loadedFile);
          formData.append('task_checklist_ids', key);
          formData.append('attachment_notes', file.note || '');
          formData.append('attachment_timings', file.timing || '');
        }
      }

      // Append text attachments
      const textData = Object.entries(textValues).filter(([, value]) => value).map(([key, value]) => ({
          task_checklist_id: key,
          value: value
      }));
      formData.append('text_attachments', JSON.stringify(textData));
      
      // Append additional files — File baru
      additionalFiles.filter(isNewFile).forEach((file) => {
          formData.append('attachments', file);
          formData.append('task_checklist_ids', '');
          formData.append('attachment_notes', (file as any).note || '');
          formData.append('attachment_timings', '');
      });

      // Append additional files — offline_ref (load dari offline storage)
      for (const file of additionalFiles.filter(isOfflineRef)) {
        const loadedFile = await loadPhotoAsFile(file.photo_ref);
        formData.append('attachments', loadedFile);
        formData.append('task_checklist_ids', '');
        formData.append('attachment_notes', file.note || '');
        formData.append('attachment_timings', '');
      }

      // Post ke endpoint baru di backend
      await axiosInstance.post(`/tasks/${task.id}/submit-feedback`, formData, {
        onUploadProgress: (e) => {
          setUploadProgress(Math.round((e.loaded * 100) / (e.total ?? e.loaded)));
        }
      });

      await submissionDraft.clear(task.id);
      showAlert(t.common?.success || 'Success', t.taskSubmission?.alerts?.submissionSuccess || 'Task report submitted successfully!', 'success', () => {
        navigate('/Task');
      });
    } catch (error: any) {
      console.error('Failed to submit task feedback:', error);
      const errorMsg = getErrorMessage(error, t.taskSubmission?.alerts?.submissionFailed || 'Failed to submit report. Please try again.');
      showAlert(t.common?.error || 'Error', errorMsg, 'error', undefined, error?.response?.data?.error || error?.message);
    } finally {
      setUploadProgress(null);
      setIsLoading(false);
    }
  };

  const handleSaveProgress = async () => {
    if (isSavingProgress || isLoading || isDraftLoading) return;

    if (!isOnline) {
      showAlert(
        t.common.warning,
        (t.taskSubmission as any)?.alerts?.saveProgressOffline ||
          'Simpan progres tidak tersedia saat offline. Gunakan tombol Submit untuk menyimpan laporan ke antrian offline.',
        'warning'
      );
      return;
    }

    if (location.lat === null || location.lng === null) {
      showAlert(t.common.warning, locationError || 'Location is required to save progress.', 'warning');
      return;
    }

    try {
      setIsSavingProgress(true);
      const formData = new FormData();
      formData.append('description', description);
      formData.append('latitude', location.lat.toString());
      formData.append('longitude', location.lng.toString());
      formData.append('address', address || '');
      
      const isNewFile = (file: any) => file instanceof File;
      const isDraftFile = (file: any) => file?.isDraft === true;

      // Get IDs of draft files to retain & their notes
      const retainedAttachmentIds: number[] = [];
      const retainedAttachmentUpdates: {id: number, note: string}[] = [];

      const processDraftFile = (file: any) => {
        if (isDraftFile(file)) {
          retainedAttachmentIds.push(file.id);
          retainedAttachmentUpdates.push({ id: file.id, note: (file as any).note || '' });
        }
      };

      Object.values(attachments).flat().forEach(processDraftFile);
      additionalFiles.forEach(processDraftFile);

      if (retainedAttachmentIds.length > 0) {
        retainedAttachmentIds.forEach(id => {
          formData.append('retained_attachment_ids', id.toString());
        });
        formData.append('retained_attachment_updates', JSON.stringify(retainedAttachmentUpdates));
      } else {
        formData.append('retained_attachment_ids', '');
      }

      // Append structured attachments
      Object.entries(attachments).forEach(([key, files]) => {
          files.filter(isNewFile).forEach(file => {
              formData.append('attachments', file);
              formData.append('task_checklist_ids', key);
              formData.append('attachment_notes', (file as any).note || '');
              formData.append('attachment_timings', (file as any).timing || '');
          });
      });

      // Append text attachments
      const textData = Object.entries(textValues).filter(([, value]) => value).map(([key, value]) => ({
          task_checklist_id: key,
          value: value
      }));
      if (textData.length > 0) {
        formData.append('text_attachments', JSON.stringify(textData));
      }

      // Append additional files
      additionalFiles.filter(isNewFile).forEach((file) => {
          formData.append('attachments', file);
          formData.append('task_checklist_ids', ''); // No specific type ID for additional files
          formData.append('attachment_notes', (file as any).note || '');
          formData.append('attachment_timings', '');
      });

      await axiosInstance.post(`/tasks/${task.id}/save-progress`, formData, {
        onUploadProgress: (e) => {
          setUploadProgress(Math.round((e.loaded * 100) / (e.total ?? e.loaded)));
        }
      });

      showAlert(t.common.success, t.taskSubmission.alerts.progressSaved, 'success');
    } catch (error: any) {
      console.error('Failed to save task progress:', error);
      const errorMsg = getErrorMessage(error, t.taskSubmission.alerts.saveProgressFailed || 'Failed to save progress.');
      showAlert(t.common.error, errorMsg, 'error', undefined, error?.response?.data?.error || error?.message);
    } finally {
      setUploadProgress(null);
      setIsSavingProgress(false);
    }
  };

  const renderUploadProgress = () => {
    if (uploadProgress === null) return null;
    return (
      <div style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40,
      }}>
        <div style={{
          backgroundColor: 'white', borderRadius: 16, padding: '28px 24px',
          width: '100%', maxWidth: 300, textAlign: 'center',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
        }}>
          <text.H5 style={{ marginBottom: 6 }}>
            {(t.taskSubmission as any)?.uploading || 'Mengupload...'}
          </text.H5>
          <text.T14 style={{ color: '#9CA3AF', marginBottom: 16 }}>
            {(t.taskSubmission as any)?.pleaseWait || 'Mohon tunggu sebentar'}
          </text.T14>
          <div style={{ backgroundColor: '#F3F4F6', borderRadius: 99, height: 10, overflow: 'hidden', marginBottom: 10 }}>
            <div style={{
              height: '100%',
              width: `${uploadProgress}%`,
              backgroundColor: theme.colors.mainColor,
              borderRadius: 99,
              transition: 'width 0.15s ease',
            }} />
          </div>
          <text.H4 style={{ color: theme.colors.mainColor }}>{uploadProgress}%</text.H4>
        </div>
      </div>
    );
  };

  const renderHeader = () => (
    <components.Header title={t.taskSubmission.title} goBack={true} />
  );


  const saveAttachment = async () => {
    if (!activeAttachmentSetting) return;

    const settingId = activeAttachmentSetting.id;
    const timing = activeAttachmentSetting.timing as string | undefined;

    // Offline: simpan file baru ke offline storage agar tidak hilang jika app ditutup
    if (!isOnline) {
      const keptDrafts = tempFiles.filter((f: any) => f.isDraft);
      const existingOfflineRefs = tempFiles.filter((f: any) => f.offline_ref);
      const newFiles = tempFiles.filter((f: any) => !f.isDraft && !f.offline_ref) as File[];

      const savedRefs: any[] = [];
      for (const file of newFiles) {
        try {
          const photoRef = await savePhotoForQueue(file);
          savedRefs.push({
            offline_ref: true,
            photo_ref: photoRef,
            name: file.name,
            type: file.type,
            note: (file as any).note || '',
            timing,
            previewUrl: URL.createObjectURL(file),
          });
        } catch { /* skip jika gagal simpan */ }
      }

      const finalFiles = [...keptDrafts, ...existingOfflineRefs, ...savedRefs];

      // Hitung state baru secara langsung agar bisa disimpan ke draft store
      const current = attachments[settingId] || [];
      const otherSlot = timing
        ? current.filter((f: any) => f.timing !== timing)
        : current.filter((f: any) => f.timing);
      const newSettingFiles = [...otherSlot, ...finalFiles];
      const newAttachments = { ...attachments, [settingId]: newSettingFiles };

      // Persist ke IndexedDB agar bertahan saat navigasi
      await persistDraft(newAttachments, additionalFiles);

      setAttachments(newAttachments);
      setActiveAttachmentSetting(null);
      return;
    }

    // File yang saat ini tersimpan untuk slot ini
    const currentSlotFiles = (attachments[settingId] || []).filter((f: any) =>
      timing ? f.timing === timing : !f.timing
    );

    // Draft file yang dihapus dari tempFiles
    const tempDraftIds = new Set(tempFiles.filter((f: any) => f.isDraft).map((f: any) => f.id));
    const removedDrafts = currentSlotFiles.filter((f: any) => f.isDraft && !tempDraftIds.has(f.id));

    // File baru yang belum diupload
    const newFiles = tempFiles.filter((f: any) => !f.isDraft);

    // Tidak ada perubahan
    if (removedDrafts.length === 0 && newFiles.length === 0) {
      setActiveAttachmentSetting(null);
      return;
    }

    setIsDraftLoading(true);
    try {
      // 1. Hapus draft yang di-remove user
      for (const file of removedDrafts) {
        try {
          await axiosInstance.delete(`/tasks/${task.id}/draft-attachments/${file.id}`);
        } catch (e) { console.error('Failed to delete draft:', e); }
      }

      // 2. Upload file-file baru satu per satu
      const uploadedFiles: any[] = [];
      for (const file of newFiles) {
        const formData = new FormData();
        formData.append('attachment', file);
        formData.append('note', (file as any).note || '');
        formData.append('task_checklist_id', settingId.toString());
        if (timing) formData.append('timing', timing);
        if (location.lat !== null && location.lng !== null) {
          formData.append('latitude', location.lat.toString());
          formData.append('longitude', location.lng!.toString());
          formData.append('address', address || '');
        }
        const response = await axiosInstance.post(`/tasks/${task.id}/draft-attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            setUploadProgress(Math.round((e.loaded * 100) / (e.total ?? e.loaded)));
          },
        });
        uploadedFiles.push({
          id: response.data.attachment.id,
          name: response.data.attachment.file_path.split(/[/\\]/).pop(),
          path: response.data.attachment.file_path,
          type: response.data.attachment.file_type,
          isDraft: true,
          note: response.data.attachment.note || '',
          timing,
        });
      }

      // 3. Final list = draft yang dipertahankan + yang baru diupload
      const keptDrafts = tempFiles.filter((f: any) => f.isDraft);
      const finalFiles = [...keptDrafts, ...uploadedFiles];

      setAttachments(prev => {
        const current = prev[settingId] || [];
        const otherSlot = timing
          ? current.filter((f: any) => f.timing !== timing)
          : current.filter((f: any) => f.timing); // non-maintenance: buang semua yang tanpa timing
        return { ...prev, [settingId]: [...otherSlot, ...finalFiles] };
      });
    } catch (error: any) {
      console.error('Failed to save attachment:', error);
      const errorMsg = getErrorMessage(error, 'Failed to save attachment.');
      showAlert(t.common?.error || 'Error', errorMsg, 'error', undefined, error?.response?.data?.error || error?.message);
      setUploadProgress(null);
      setIsDraftLoading(false);
      return;
    } finally {
      setUploadProgress(null);
      setIsDraftLoading(false);
    }
    setActiveAttachmentSetting(null);
  };

  const renderUploadScreen = () => {
    if (!activeAttachmentSetting) return null;

    return (
        // Lebar mengikuti #app (max 650px, di tengah) — tanpa ini di PC melebar selebar browser
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            width: '100%', maxWidth: 650, margin: '0 auto',
            backgroundColor: 'white', zIndex: 2000,
            display: 'flex', flexDirection: 'column'
        }}>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            {/* Header */}
            <div style={{
                height: 60, display: 'flex', alignItems: 'center', padding: '0 20px', 
                borderBottom: '1px solid #eee', backgroundColor: 'white'
            }}>
                <div onClick={() => setActiveAttachmentSetting(null)} style={{marginRight: 15, cursor: 'pointer'}}>
                    <CloseIcon size={24} color={theme.colors.mainDark} />
                </div>
                <text.H4>
                  {activeAttachmentSetting.title}
                  {activeAttachmentSetting?.is_maintenance && activeAttachmentSetting.timing ? ` - ${activeAttachmentSetting.timing === 'before' ? t.taskSubmission.before : t.taskSubmission.after}` : ''}
                </text.H4>
            </div>

            <div className='container' style={{padding: 20, flex: 1, overflowY: 'auto'}}>
                {/* Example Image */}
                <div style={{marginBottom: 24}}>
                    <text.H5 style={{marginBottom: 10}}>{t.taskSubmission.uploadScreen.example}</text.H5>
                    {activeAttachmentSetting.example ? (
                        <img 
                            src={`${imageBaseUrl}${activeAttachmentSetting.example}`} 
                            alt="Example" 
                            style={{
                                width: '100%', height: 200, objectFit: 'contain', 
                                backgroundColor: '#F3F4F6', borderRadius: 10, border: '1px solid #E5E7EB'
                            }} 
                        />
                    ) : (
                        <div style={{
                            width: '100%', height: 200, backgroundColor: '#F3F4F6', 
                            borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: '1px dashed #D1D5DB'
                        }}>
                            <text.T14 style={{color: '#9CA3AF'}}>{t.taskSubmission.uploadScreen.imageExample}</text.T14>
                        </div>
                    )}
                </div>

                {/* Description */}
                <div style={{marginBottom: 20}}>
                    <div style={{...utils.rowCenterSpcBtw(), marginBottom: 10}}>
                        <text.H5>{t.common.description}</text.H5>
                     </div>
                    <text.T14 style={{color: '#6B7280', whiteSpace: 'pre-wrap', lineHeight: 1.6}}>
                      {activeAttachmentSetting.description}
                    </text.T14>
                </div>

                {/* Selected Files Preview */}
                <div style={{marginBottom: 24}}>
                  <div style={{...utils.rowCenterSpcBtw(), marginBottom: 10}}>
                    <text.H5>{(t.taskSubmission as any)?.yourFiles || t.taskSubmission.uploadScreen.yourFile}</text.H5>
                    {tempFiles.length > 0 && (
                      <text.T14 style={{color: '#9CA3AF'}}>{tempFiles.length} file{tempFiles.length > 1 ? 's' : ''}</text.T14>
                    )}
                  </div>

                  {isCompressing ? (
                    <div style={{
                      height: 110, border: '2px dashed #D1D5DB', borderRadius: 10,
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      justifyContent: 'center', backgroundColor: '#F9FAFB', gap: 12,
                    }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%',
                        border: '3px solid #E5E7EB', borderTopColor: theme.colors.mainColor,
                        animation: 'spin 0.8s linear infinite',
                      }} />
                      <text.T14 style={{color: '#6B7280'}}>
                        {(t.taskSubmission as any)?.compressing || 'Memproses gambar...'}
                      </text.T14>
                    </div>
                  ) : (
                    <div style={{display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8}}>
                      {/* Tombol tambah — di kiri agar selalu terlihat tanpa scroll */}
                      <div
                        onClick={() => tempFileInputRef.current?.click()}
                        style={{
                          flexShrink: 0, width: 100, height: 100, borderRadius: 10,
                          border: '2px dashed #D1D5DB', display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                          backgroundColor: '#F9FAFB',
                        }}
                      >
                        <CameraIcon size={28} color="#9CA3AF" />
                        <text.T14 style={{color: '#9CA3AF', fontSize: 12, marginTop: 4}}>
                          {tempFiles.length > 0
                            ? (t.taskSubmission as any)?.addMore || '+ Add'
                            : t.taskSubmission.uploadScreen.tapToUpload}
                        </text.T14>
                      </div>
                      {tempFiles.map((file: any, index: number) => {
                        const isDraft = !!file.isDraft;
                        const isImage = file.type?.startsWith('image/');
                        const imageUrl = isDraft ? `${imageBaseUrl}${file.path}` : file.offline_ref ? (file.previewUrl || '') : URL.createObjectURL(file);
                        return (
                          <div key={index} style={{position: 'relative', flexShrink: 0, width: 100, height: 100}}>
                            <div style={{
                              width: '100%', height: '100%', borderRadius: 10, overflow: 'hidden',
                              border: '1px solid #E5E7EB', backgroundColor: '#F3F4F6',
                            }}>
                              {isImage ? (
                                <img
                                  src={imageUrl}
                                  alt="preview"
                                  style={{width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer'}}
                                  onClick={() => setTempPreviewUrl(imageUrl)}
                                />
                              ) : (
                                <div style={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 4}}>
                                  {getFileIcon(file.name)}
                                  <text.T14 style={{color: '#9CA3AF', fontSize: 10, marginTop: 4, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%'}}>
                                    {file.name}
                                  </text.T14>
                                </div>
                              )}
                            </div>
                            {/* X button di dalam batas thumbnail agar tidak terpotong scroll container */}
                            <div
                              onClick={(e) => { e.stopPropagation(); removeTempFile(index); }}
                              style={{
                                position: 'absolute', top: 4, right: 4,
                                backgroundColor: 'rgba(0,0,0,0.55)', borderRadius: 10,
                                width: 20, height: 20,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer',
                              }}
                            >
                              <CloseIcon size={11} color="white" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Note bersama (berlaku untuk semua file baru) */}
                  {tempFiles.length > 0 && (
                    <div style={{marginTop: 16}}>
                      <text.T14 style={{marginBottom: 8, display: 'block', color: '#6B7280'}}>{t.taskDetails.notes}</text.T14>
                      <input
                        type="text"
                        placeholder={t.taskDetails.reviewModal.addNotes}
                        value={(tempFiles[0] as any).note || ''}
                        onChange={(e) => {
                          const note = e.target.value;
                          setTempFiles(prev => prev.map((f: any) => { (f as any).note = note; return f; }));
                        }}
                        style={{
                          width: '100%', padding: '10px 14px', borderRadius: 8,
                          border: '1px solid #E5E7EB', fontSize: 16, outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  )}
                </div>
            </div>
            
            <div style={{padding: '16px 24px 32px', borderTop: '1px solid #E5E7EB', backgroundColor: 'white'}}>
                <div className='container' style={{padding: 0, maxWidth: 650, margin: '0 auto'}}>
                    {isDraftLoading && uploadProgress !== null ? (
                      <div style={{ padding: '4px 0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <text.T14 style={{ color: theme.colors.mainColor, fontWeight: '600' }}>
                            {(t.taskSubmission as any)?.uploading || 'Mengupload...'}
                          </text.T14>
                          <text.T14 style={{ color: theme.colors.mainColor, fontWeight: '700' }}>
                            {uploadProgress}%
                          </text.T14>
                        </div>
                        <div style={{ backgroundColor: '#E5E7EB', borderRadius: 99, height: 8, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            width: `${uploadProgress}%`,
                            backgroundColor: theme.colors.mainColor,
                            borderRadius: 99,
                            transition: 'width 0.15s ease',
                          }} />
                        </div>
                      </div>
                    ) : (
                      <components.Button title={t.common.save} onClick={saveAttachment} />
                    )}
                </div>
            </div>

            <input
                type="file"
                accept="image/*"
                ref={tempFileInputRef}
                style={{display: 'none'}}
                onChange={handleTempFileChange}
            />

            {/* Lightbox preview */}
            {tempPreviewUrl && (
              <div
                onClick={() => setTempPreviewUrl(null)}
                style={{
                  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)',
                  zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <img
                  src={tempPreviewUrl}
                  alt="preview"
                  style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'}}
                />
                <div
                  style={{
                    position: 'absolute', top: 16, right: 16,
                    backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20,
                    width: 36, height: 36, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', cursor: 'pointer',
                  }}
                >
                  <CloseIcon size={20} color="white" />
                </div>
              </div>
            )}
        </div>
    );
  };

  const renderAlert = () => {
    if (!alertConfig.isOpen) return null;
    return (
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 4000,
          display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
        }}
      >
        <div
          style={{
            backgroundColor: 'white', borderRadius: 16, padding: 24,
            width: '100%', maxWidth: 320, textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            display: 'flex', flexDirection: 'column', alignItems: 'center'
          }}
        >
          <div style={{marginBottom: 16}}>
            {alertConfig.type === 'success' && <CheckCircleIcon size={60} color="#10B981" />}
            {alertConfig.type === 'error' && <ErrorIcon size={60} color="#EF4444" />}
            {alertConfig.type === 'warning' && <WarningIcon size={60} color="#F59E0B" />}
          </div>
          <text.H4 style={{ marginBottom: 8 }}>{alertConfig.title}</text.H4>
          <text.T16 style={{ marginBottom: alertConfig.errorDetail ? 12 : 24, color: theme.colors.bodyTextColor, lineHeight: 1.5 }}>
            {alertConfig.message}
          </text.T16>
          {alertConfig.errorDetail && (
            <div style={{ width: '100%', marginBottom: 24, textAlign: 'left' }}>
              <button
                onClick={() => setErrorDetailExpanded(prev => !prev)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9CA3AF', fontSize: 12, textDecoration: 'underline' }}
              >
                {errorDetailExpanded ? 'hide error details' : 'click to expand error details'}
              </button>
              {errorDetailExpanded && (
                <div style={{ marginTop: 8, padding: 10, backgroundColor: '#FEF2F2', borderRadius: 8, border: '1px solid #FECACA', fontSize: 11, color: '#DC2626', lineHeight: 1.5, wordBreak: 'break-all' }}>
                  {alertConfig.errorDetail}
                </div>
              )}
            </div>
          )}
          <components.Button title={t.common.ok} onClick={hideAlert} containerStyle={{width: '100%'}} />
        </div>
      </div>
    );
  };

  const renderContent = () => (
    <div style={{paddingBottom: 100}}>
      {/* Task Info Summary */}
      <div style={{marginBottom: 24, padding: 16, backgroundColor: '#F9FAFB', borderRadius: 10}}>
        <text.T14 style={{color: '#6B7280'}}>{t.taskSubmission.reportingFor}</text.T14>
        <text.H5>{fullTask?.task_type}</text.H5>
        {/* Task Type Select */}
        {/*<select
          value={task?.task_type}
          onChange={(e) => setTask(prev => ({...prev, task_type: e.target.value}))}
        >
          {taskTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>*/}
        <text.T14 style={{color: '#9CA3AF', marginTop: 4}} numberOfLines={1}>{fullTask?.description}</text.T14>

        {/* Task Type Options */}


      </div>

      {/* Description Input */}
      <div style={{marginBottom: 24}}>
        <text.H5 style={{marginBottom: 10}}>{t.common.description}</text.H5>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t.taskSubmission.describeWork}
          style={{
            width: '100%',
            height: 120,
            padding: 14,
            borderRadius: 10,
            border: '1px solid #E5E7EB',
            fontFamily: 'inherit',
            fontSize: 16,
            resize: 'none',
            boxSizing: 'border-box',
            whiteSpace: 'pre-wrap'
          }}
        />
      </div>

      {/* Attachments Upload */}
      {attachmentSettings.length > 0 && <>
      <text.H5 style={{marginBottom: 10}}>{t.section.requiredAttachments}</text.H5>
      {Array.isArray(attachmentSettings) && [...attachmentSettings].sort((a, b) => {
        if (a.input_type === 'text' && b.input_type !== 'text') return -1;
        if (a.input_type !== 'text' && b.input_type === 'text') return 1;
        return 0;
      }).map((setting) => {
        if (setting.input_type === 'text') {
          return (
            <div key={setting.id} style={{marginBottom: 20}}>
              <text.H5 style={{marginBottom: 4}}>{setting.title} <span style={{color: 'red'}}>*</span></text.H5>
              {setting.description && (
                <text.T14 style={{marginBottom: 8, color: '#6B7280', display: 'block', lineHeight: 1.6, whiteSpace: 'pre-wrap'}}>
                  {setting.description}
                </text.T14>
              )}
              <input
                type="text"
                value={textValues[setting.id] || ''}
                onChange={(e) => handleTextChange(setting.id, e.target.value)}
                placeholder={setting.example}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: 10,
                  border: '1px solid #E5E7EB',
                  fontSize: 16,
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  outline: 'none'
                }}
              />
            </div>
          );
        }

        if (isDraftLoading) {
             return <div key={setting.id} style={{marginBottom: 20}}><text.T14 style={{color: '#9CA3AF', fontStyle: 'italic'}}>Loading {setting.title}...</text.T14></div>;
        }

        const existingFiles = attachments[setting.id] || [];

        if (setting.is_maintenance) {
          const renderTimingStrip = (timingLabel: string, timingKey: string) => {
            const slotFiles = existingFiles.filter((f: any) => f.timing === timingKey);
            return (
              <div style={{flex: 1}}>
                <text.T14 style={{marginBottom: 6, fontWeight: '600'}}>{timingLabel}</text.T14>
                <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                  {slotFiles.map((file: any, idx: number) => {
                    const isImage = file.type?.startsWith('image/');
                    const imageUrl = file.isDraft ? `${imageBaseUrl}${file.path}` : file.offline_ref ? (file.previewUrl || '') : URL.createObjectURL(file);
                    return (
                      <div
                        key={file.id || idx}
                        onClick={() => handleAttachmentClick(setting, timingKey)}
                        style={{width: 60, height: 60, borderRadius: 8, overflow: 'hidden', border: '1px solid #E5E7EB', cursor: 'pointer', flexShrink: 0, backgroundColor: '#F9FAFB'}}
                      >
                        {isImage
                          ? <img src={imageUrl} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                          : <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>{getFileIcon(file.name)}</div>
                        }
                      </div>
                    );
                  })}
                  <div
                    onClick={() => handleAttachmentClick(setting, timingKey)}
                    style={{
                      width: 60, height: 60, borderRadius: 8, border: '2px dashed #D1D5DB',
                      cursor: 'pointer', display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center', backgroundColor: '#F9FAFB', flexShrink: 0,
                    }}
                  >
                    <CameraIcon size={20} color="#9CA3AF" />
                    {slotFiles.length === 0 && <text.T14 style={{color: '#9CA3AF', fontSize: 10, marginTop: 2}}>Add</text.T14>}
                  </div>
                </div>
              </div>
            );
          };
          return (
            <div key={setting.id} style={{marginBottom: 20}}>
              <text.H5 style={{marginBottom: 10}}>{setting.title} <span style={{color: 'red'}}>*</span></text.H5>
              <div style={{display: 'flex', gap: 10}}>
                {renderTimingStrip(t.taskSubmission.before, 'before')}
                {renderTimingStrip(t.taskSubmission.after, 'after')}
              </div>
            </div>
          );
        } else {
          return (
            <div key={setting.id} style={{marginBottom: 20}}>
              <text.H5 style={{marginBottom: 8}}>{setting.title} <span style={{color: 'red'}}>*</span></text.H5>
              <div style={{display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start'}}>
                {existingFiles.map((file: any, idx: number) => {
                  const isImage = file.type?.startsWith('image/');
                  const imageUrl = file.isDraft ? `${imageBaseUrl}${file.path}` : file.offline_ref ? (file.previewUrl || '') : URL.createObjectURL(file);
                  return (
                    <div
                      key={file.id || idx}
                      onClick={() => handleAttachmentClick(setting, undefined)}
                      style={{width: 80, height: 80, borderRadius: 10, overflow: 'hidden', border: '1px solid #E5E7EB', cursor: 'pointer', flexShrink: 0, backgroundColor: '#F9FAFB'}}
                    >
                      {isImage
                        ? <img src={imageUrl} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                        : <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>{getFileIcon(file.name)}</div>
                      }
                    </div>
                  );
                })}
                {/* Tombol tambah */}
                <div
                  onClick={() => handleAttachmentClick(setting, undefined)}
                  style={{
                    width: 80, height: 80, borderRadius: 10,
                    border: '2px dashed #D1D5DB', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: '#F9FAFB', flexShrink: 0,
                  }}
                >
                  <CameraIcon size={24} color="#9CA3AF" />
                  {existingFiles.length === 0 && (
                    <text.T14 style={{color: '#9CA3AF', fontSize: 11, marginTop: 4}}>
                      {(t.taskSubmission as any)?.addPhoto || 'Add'}
                    </text.T14>
                  )}
                </div>
              </div>
            </div>
          );
        }
      })}
      </>}

        <input
          type="file"
          multiple
          accept="image/*"
          ref={fileInputRef}
          style={{display: 'none'}}
          onChange={handleFileChange}
        />

        {/* Additional Attachments */}
        <div style={{marginBottom: 24, marginTop: 20}}>
            <div style={{...utils.rowCenterSpcBtw(), marginBottom: 10}}>
                <text.H5>{t.common.attachments}</text.H5>
                <div onClick={() => additionalFileInputRef.current?.click()} style={{cursor: 'pointer'}}>
                    <text.T14 style={{color: theme.colors.mainColor}}>
                        {t.taskSubmission.addFiles}
                    </text.T14>
                </div>
            </div>
            
            <input
                type="file"
                multiple
                accept="image/*"
                ref={additionalFileInputRef}
                style={{display: 'none'}}
                onChange={handleAdditionalFileChange}
            />

            {isDraftLoading ? (
                <text.T14 style={{color: '#9CA3AF', fontStyle: 'italic'}}>Loading additional files...</text.T14>
            ) : (
              <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                {additionalFiles.map((file, index) => (
                    <div key={file.id || index} style={{
                        display: 'flex', 
                        padding: 12, 
                        border: '1px solid #E5E7EB', 
                        borderRadius: 12, 
                        backgroundColor: '#F9FAFB',
                        alignItems: 'flex-start'
                    }}>
                        {/* Thumbnail */}
                        <div style={{
                            position: 'relative', width: 80, height: 80, 
                            borderRadius: 8, overflow: 'hidden', backgroundColor: '#fff',
                            border: '1px solid #E5E7EB', flexShrink: 0, marginRight: 16,
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            {(() => {
                                const isDraft = !!file.isDraft;
                                const isImage = file.type?.startsWith('image/');
                                const imageUrl = isDraft ? `${imageBaseUrl}${file.path}` : file.offline_ref ? (file.previewUrl || '') : URL.createObjectURL(file);

                                return isImage ? (
                                    <img 
                                        src={imageUrl} 
                                        alt={`preview-${index}`} 
                                        style={{width: '100%', height: '100%', objectFit: 'cover'}} 
                                    />
                                ) : (
                                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 4, textAlign: 'center', width: '100%'}}>
                                        {getFileIcon(file.name)}
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Details */}
                        <div style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8}}>
                                <text.T14 style={{fontWeight: '600', color: theme.colors.mainDark, wordBreak: 'break-all', fontSize: 14}} numberOfLines={1}>
                                    {file.name}
                                </text.T14>
                                <div 
                                    onClick={() => removeAdditionalFile(index)}
                                    style={{cursor: 'pointer', padding: 4, marginTop: -4, marginRight: -4}}
                                >
                                    <DeleteIcon size={20} color="#EF4444" />
                                </div>
                            </div>
                            
                            <input
                                type="text"
                                placeholder={t.taskDetails.reviewModal.addNotes}
                                value={(file as any).note || ''}
                                onChange={(e) => {
                                    const updated = [...additionalFiles];
                                    (updated[index] as any).note = e.target.value;
                                    setAdditionalFiles(updated);
                                }}
                                style={{
                                    width: '100%', padding: '8px 12px', borderRadius: 8,
                                    border: '1px solid #E5E7EB', fontSize: 14, outline: 'none',
                                    backgroundColor: 'white',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div>
                ))}
                {additionalFiles.length === 0 && (
                    <text.T14 style={{color: '#9CA3AF', fontStyle: 'italic'}}>{t.taskSubmission.noFiles}</text.T14>
                )}
            </div>
            )}
        </div>

      {/* Location Status (Hidden logic, visible status) */}
      <div style={{marginBottom: 24}}>
        <text.H5 style={{marginBottom: 6}}>{t.common.location}</text.H5>
        {location.lat ? (
            <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
                <div style={{display: 'flex', alignItems: 'center', gap: 8, color: 'green'}}>
                    <svg.CheckSvg /> 
                    <text.T14>{t.taskSubmission.locationAcquired}</text.T14>
                </div>
                {address && (
                    <text.T14 style={{color: '#4B5563', fontSize: 13, marginLeft: 28, lineHeight: 1.4}}>
                        {address}
                    </text.T14>
                )}
                <text.T14 style={{color: '#9CA3AF', fontSize: 12, marginLeft: 28}}>
                    {location.lat.toFixed(6)}, {location.lng?.toFixed(6)}
                </text.T14>
            </div>
        ) : (
            <text.T14 style={{color: locationError ? 'red' : 'orange'}}>
                {locationError || t.taskSubmission.fetchingLocation}
            </text.T14>
        )}
      </div>
    </div>
  );

  const renderButton = () => {
    return (
      <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          backgroundColor: 'white', padding: '16px 24px 32px',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.05)',
          zIndex: 10
      }}>
          <div className='container' style={{padding: 0, maxWidth: 650, margin: '0 auto', display: 'flex', flexDirection: 'row', gap: 12}}>
              <components.Button 
                title={isSavingProgress ? t.common.savingProgress : t.common.saveProgress}
                onClick={handleSaveProgress}
                style={{
                  backgroundColor: '#F3F4F6', 
                  color: '#4B5563',
                  opacity: isLoading || isSavingProgress || isDraftLoading || location.lat === null ? 0.5 : 1
                }}
                containerStyle={{flex: 1}}
              />
              <components.Button
                title={isLoading ? t.common.submitting : t.common.submit}
                onClick={handleSubmit}
                style={{opacity: isLoading || isSavingProgress || isDraftLoading || location.lat === null ? 0.5 : 1}}
                containerStyle={{flex: 1}}
              />
          </div>
      </div>
    );
  };

  return (
    <div id='screen' style={{backgroundColor: '#FFFFFF', minHeight: '100vh'}}>
      {renderHeader()}
      <main className='container' style={{paddingTop: 60}}>
        {renderContent()}
      </main>
      {renderUploadScreen()}
      {renderButton()}
      {renderAlert()}
      {renderUploadProgress()}
    </div>
  );

};